import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAutoLogout } from "../hooks/UseAutoLogout";
import CustomButton from "../Buttons/CustomButton";
import defaultAvatar from "./../../assests/avatar.jpg";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { showToast } from "../Buttons/Toaster";
import ReactCountryFlag from "react-country-flag";
import { Link } from "react-router-dom";
import validator from "validator";
import { head } from "lodash";

const getToastStyle = () => ({
  background: "#ffebee",
  color: "#d32f2f",
  width: "fit-content",
  minWidth: "fit-content",
  textAlign: "left",
  top: "2px",
  borderRadius: "8px",
  fontSize: "14px",
  padding: "12px",
});

const EditProfile = ({
  profile,
  formData,
  setFormData,
  errors,
  setErrors,
  selectedCountry,
  setSelectedCountry,
  isLoading,
  onCancel,
  onSave,
}) => {
  const fileInputRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [digitCount, setDigitCount] = useState(0);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const[profileformData,setProfileFormData]=useState()
  
  // Enhanced loading state - show loader if no profile OR if form data is empty
  const [profileLoading, setProfileLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);

  // TextSkeleton component matching UserManagement styling
  const TextSkeleton = ({
    width = "100%",
    height = "16px",
    className = "",
    style = {},
  }) => (
    <div
      className={className}
      style={{
        width,
        height,
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        borderRadius: "4px",
        animation: "pulse 1.5s infinite",
        ...style,
      }}
    />
  );

  const [dropdownOptions] = useState({
    countries: [
      { id: "India", name: "India", code: "IN", dialCode: "+91" },
      {
        id: "United States",
        name: "United States",
        code: "US",
        dialCode: "+1",
      },
      {
        id: "United Kingdom",
        name: "United Kingdom",
        code: "GB",
        dialCode: "+44",
      },
      { id: "Canada", name: "Canada", code: "CA", dialCode: "+1" },
      { id: "Australia", name: "Australia", code: "AU", dialCode: "+61" },
      { id: "Japan", name: "Japan", code: "JP", dialCode: "+81" },
      { id: "Germany", name: "Germany", code: "DE", dialCode: "+49" },
      { id: "France", name: "France", code: "FR", dialCode: "+33" },
      { id: "Brazil", name: "Brazil", code: "BR", dialCode: "+55" },
      { id: "China", name: "China", code: "CN", dialCode: "+86" },
    ],
    languages: [
      { id: "Telugu", name: "Telugu" },
      { id: "Tamil", name: "Tamil" },
      { id: "English", name: "English" },
      { id: "Hindi", name: "Hindi" },
      { id: "Spanish", name: "Spanish" },
      { id: "French", name: "French" },
      { id: "German", name: "German" },
      { id: "Japanese", name: "Japanese" },
      { id: "Portuguese", name: "Portuguese" },
      { id: "Russian", name: "Russian" },
      { id: "Chinese", name: "Chinese" },
      { id: "Arabic", name: "Arabic" },
    ],
    timeZones: [
      { id: "UTC", name: "UTC" },
      {
        id: "Indian Standard Time (IST) - UTC+5:30",
        name: "Indian Standard Time (IST) - UTC+5:30",
      },
      {
        id: "Eastern Standard Time (EST) - UTC-5",
        name: "Eastern Standard Time (EST) - UTC-5",
      },
      {
        id: "Pacific Standard Time (PST) - UTC-8",
        name: "Pacific Standard Time (PST) - UTC-8",
      },
      {
        id: "Central Standard Time (CST) - UTC-6",
        name: "Central Standard Time (CST) - UTC-6",
      },
      { id: "Greenwich Mean Time (GMT)", name: "Greenwich Mean Time (GMT)" },
      {
        id: "Central European Time (CET) - UTC+1",
        name: "Central European Time (CET) - UTC+1",
      },
      {
        id: "Australian Eastern Standard Time (AEST) - UTC+10",
        name: "Australian Eastern Standard Time (AEST) - UTC+10",
      },
      {
        id: "Japan Standard Time (JST) - UTC+9",
        name: "Japan Standard Time (JST) - UTC+9",
      },
      {
        id: "British Summer Time (BST) - UTC+1",
        name: "British Summer Time (BST) - UTC+1",
      },
    ],
    currencies: [
      { id: "Indian Rupee (INR)", name: "Indian Rupee (INR)" },
      { id: "US Dollar (USD)", name: "US Dollar (USD)" },
      { id: "Euro (EUR)", name: "Euro (EUR)" },
      { id: "British Pound (GBP)", name: "British Pound (GBP)" },
      { id: "Japanese Yen (JPY)", name: "Japanese Yen (JPY)" },
      { id: "Australian Dollar (AUD)", name: "Australian Dollar (AUD)" },
      { id: "Canadian Dollar (CAD)", name: "Canadian Dollar (CAD)" },
      { id: "Chinese Yuan (CNY)", name: "Chinese Yuan (CNY)" },
      { id: "Russian Ruble (RUB)", name: "Russian Ruble (RUB)" },
      { id: "Brazilian Real (BRL)", name: "Brazilian Real (BRL)" },
    ],
  });

  const findCountryByPhone = (phoneNumber, countryList) => {
    if (!phoneNumber || phoneNumber.trim() === "") return null;

    const cleanPhone = phoneNumber.replace(/[^+\d]/g, "");
    const sortedCountries = [...countryList].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );

    return sortedCountries.find((country) =>
      cleanPhone.startsWith(country.dialCode)
    );
  };

  // Check if form data has meaningful values (not just empty strings)
  const hasFormData = useMemo(() => {
    return (
      formData.first_name?.trim() ||
      formData.last_name?.trim() ||
      formData.email?.trim() ||
      formData.company?.trim() ||
      formData.contact_phone?.trim()
    );
  }, [formData]);

  // Enhanced loading logic
  useEffect(() => {
    const shouldShowLoader = !profile || !hasFormData;
    setProfileLoading(shouldShowLoader);
    setIsDataReady(!shouldShowLoader);
  }, [profile, hasFormData]);

  // Fetch profile data when component mounts or when profile is not available
  useEffect(() => {
    const fetchProfileData = async () => {
      const apiBaseProfileData = process.env.REACT_APP_API_URL_CODE_CONVERSION;
      try {
        setProfileLoading(true);
        setIsDataReady(false);
        
        const authToken = sessionStorage.getItem("access_token");
        if (!authToken) throw new Error("Authentication token missing.");

        const response = await axios.get(`${apiBaseProfileData}/view_profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response?.data) {
          // Update form data with the fetched profile
          setFormData((prev) => ({
            ...prev,
            first_name: response.data.first_name || "",
            last_name: response.data.last_name || "",
            email: response.data.email || "",
            company: response.data.company || "",
            contact_phone: response.data.contact_phone || "",
            company_site: response.data.company_site || "",
            country: response.data.country || "",
            language: response.data.language || "",
            time_zone: response.data.time_zone || "",
            currency: response.data.currency || "",
            communication: response.data.communication
              ? response.data.communication.split(",")
              : ["Email"],
            allow_marketing: response.data.allow_marketing || false,
            avatar: response.data.avatar || null,
          }));

          // Set selected country based on phone number
          const detectedCountry = findCountryByPhone(
            response.data.contact_phone,
            dropdownOptions.countries
          );
          if (detectedCountry) {
            setSelectedCountry(detectedCountry);
          }
          
          setIsDataReady(true);
        }
      } catch (error) {
        console.error("Error fetching profile data in EditProfile:", error);
        setIsDataReady(false);
      } finally {
        setProfileLoading(false);
      }
    };

    // Fetch if we don't have profile data OR if form data is empty
    if (!profile || !hasFormData) {
      fetchProfileData();
    } else {
      setProfileLoading(false);
      setIsDataReady(true);
    }
  }, [profile, hasFormData]); // Add hasFormData to dependency array

  // Update form data when profile prop changes
  useEffect(() => {
    if (profile) {
      setProfileFormData((prev) => ({
        ...prev,
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        email: profile.email || "",
        company: profile.company || "",
        contact_phone: profile.contact_phone || "",
        company_site: profile.company_site || "",
        country: profile.country || "",
        language: profile.language || "",
        time_zone: profile.time_zone || "",
        currency: profile.currency || "",
        communication: profile.communication
          ? profile.communication.split(",")
          : ["Email"],
        allow_marketing: profile.allow_marketing || false,
        avatar: profile.avatar || null,
      }));
      
      // Set selected country based on phone number
      const detectedCountry = findCountryByPhone(
        profile.contact_phone,
        dropdownOptions.countries
      );
      if (detectedCountry) {
        setSelectedCountry(detectedCountry);
      }
      
      setIsDataReady(true);
    }
  }, [profile]);

  useEffect(() => {
    if (formData.contact_phone) {
      const phoneWithoutCode = formData.contact_phone.replace(
        new RegExp(`^${escapeRegExp(selectedCountry?.dialCode || "")}\\s?`),
        ""
      );
      setDigitCount(phoneWithoutCode.replace(/\D/g, "").length);
    }
  }, []);

  const sortedCountries = useMemo(
    () =>
      [...dropdownOptions.countries].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [dropdownOptions]
  );

  const sortedLanguages = useMemo(
    () =>
      [...dropdownOptions.languages].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [dropdownOptions]
  );

  const sortedTimeZones = useMemo(
    () =>
      [...dropdownOptions.timeZones].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [dropdownOptions]
  );

  const sortedCurrencies = useMemo(
    () =>
      [...dropdownOptions.currencies].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [dropdownOptions]
  );

  const { state } = useAuth();
  const navigate = useNavigate();
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  useEffect(() => {
    localStorage.setItem("activeTab", "v-pills-profile");
    if (isCollapsed) {
      document.body.classList.add("vertical-collpsed");
    }
    return () => {
      document.body.classList.remove("vertical-collpsed");
    };
  }, [isCollapsed]);

  useAutoLogout();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (type === "checkbox") {
      if (name === "communication") {
        const updatedComms = formData.communication.includes(value)
          ? formData.communication.filter((item) => item !== value)
          : [...formData.communication, value];
        setFormData((prev) => ({ ...prev, communication: updatedComms }));
        if (updatedComms.length > 0) {
          setErrors((prev) => ({ ...prev, communication: "" }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const currentDialCode = selectedCountry?.dialCode || "";
    const escapedDialCode = escapeRegExp(currentDialCode);

    const phoneWithoutCode = value.replace(
      new RegExp(`^${escapedDialCode}\\s?`),
      ""
    );
    const newDigitCount = phoneWithoutCode.replace(/\D/g, "").length;
    setDigitCount(newDigitCount);

    if (currentDialCode && value.startsWith(currentDialCode)) {
      const formattedValue = value.replace(
        new RegExp(`^(${escapedDialCode}\\s?)(\\d*)`),
        (match, p1, p2) => {
          if (p1.endsWith(currentDialCode)) {
            return `${currentDialCode} ${p2}`;
          }
          return match;
        }
      );

      if (
        /^\+?[\d\s]{0,20}$/.test(formattedValue) &&
        (formattedValue.match(/\s/g) || []).length <= 2 &&
        formattedValue.replace(/[^\d]/g, "").length <= 20
      ) {
        e.target.value = formattedValue;
        handleChange(e);
      }
    } else {
      if (
        /^\+?[\d\s]{0,20}$/.test(value) &&
        (value.match(/\s/g) || []).length <= 1 &&
        value.replace(/[^\d]/g, "").length <= 20
      ) {
        handleChange(e);
      }
    }

    if (errors.contact_phone) {
      setErrors((prev) => ({ ...prev, contact_phone: "" }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);

    const currentDialCode = selectedCountry?.dialCode || "";
    const escapedDialCode = escapeRegExp(currentDialCode);

    const phoneWithoutCode = formData.contact_phone.replace(
      new RegExp(`^${escapedDialCode}\\s?`),
      ""
    );

    setFormData((prev) => ({
      ...prev,
      contact_phone:
        country.dialCode + (phoneWithoutCode ? " " + phoneWithoutCode : ""),
    }));
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    if (formData[name] === "") {
      setFormData((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024;
    if (file) {
      if (file.size > maxSize) {
        showToast("File size too large! Maximum allowed is 2MB.", "error");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          avatar: reader.result,
          avatarFile: file,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setFormData((prev) => ({ ...prev, avatar: null, avatarFile: null }));
  };

  const formatDropdownValue = (fieldType, code) => {
    if (!code) return "";
    const knownOption = dropdownOptions[fieldType]?.find((c) => c.id === code);
    if (knownOption) return knownOption.name;

    switch (fieldType) {
      case "countries":
        return code.length === 2 ? code.toUpperCase() : code;
      case "languages":
        return code.toUpperCase();
      case "timeZones":
        return code.toUpperCase();
      case "currencies":
        return code.toUpperCase();
      default:
        return code;
    }
  };

  const getCombinedOptions = (fieldType, currentValue) => {
    const predefinedOptions = dropdownOptions[fieldType] || [];
    const hasValueInOptions = predefinedOptions.some(
      (c) => c.id === currentValue
    );

    if (currentValue && !hasValueInOptions) {
      return [
        {
          id: currentValue,
          name: formatDropdownValue(fieldType, currentValue),
        },
        ...predefinedOptions,
      ];
    }
    return predefinedOptions;
  };

  // Show skeleton loader if data is not ready
  if (!isDataReady || profileLoading) {
    return (
      <div
        id="home"
        className={`tab_content font-family-inter`}
        style={{ marginTop: "-70px" }}
      >
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 0.6; }
              50% { opacity: 0.3; }
              100% { opacity: 0.6; }
            }
          `}
        </style>
        <div className="d-flex">
          <div className="main-content index-bg ms-0 w-100">
            <div className="page-content w-100">
              <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center">
                  <div></div>
                  <div className="d-flex gap-2"></div>
                </div>
               <p className="Profilesettinghead">Profile Settings</p> 
              </div>

              <Container fluid className="my-1 scrollable-container">
                {/* Avatar Upload Section Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="60px" height="20px" />
                  </Col>
                  <Col md={9}>
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                          animation: "pulse 1.5s infinite",
                          border: "1px solid #dee2e6",
                          marginRight: "12px",
                        }}
                      />
                      <div className="d-flex gap-3 align-items-center">
                        <TextSkeleton
                          width="120px"
                          height="36px"
                          style={{ borderRadius: "4px" }}
                        />
                        <TextSkeleton
                          width="120px"
                          height="36px"
                          style={{ borderRadius: "4px" }}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center mt-1">
                      <TextSkeleton width="300px" height="14px" />
                    </div>
                  </Col>
                </Row>

                {/* Full Name Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="80px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <div className="row g-3">
                      <Col md={6}>
                        <TextSkeleton
                          width="100%"
                          height="45px"
                          style={{ borderRadius: "4px" }}
                        />
                      </Col>
                      <Col md={6}>
                        <TextSkeleton
                          width="100%"
                          height="45px"
                          style={{ borderRadius: "4px" }}
                        />
                      </Col>
                    </div>
                  </Col>
                </Row>

                {/* Email Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="50px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="45px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Company Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="70px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="45px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Contact Phone Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="100px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="45px"
                      style={{ borderRadius: "4px" }}
                    />
                    <div className="d-flex align-items-center mt-1">
                      <TextSkeleton width="250px" height="14px" />
                    </div>
                  </Col>
                </Row>

                {/* Company Site Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="90px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="45px"
                      style={{ borderRadius: "4px" }}
                    />
                    <div className="d-flex align-items-center mt-1">
                      <TextSkeleton width="200px" height="14px" />
                    </div>
                  </Col>
                </Row>

                {/* Country Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="60px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="38px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Language Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="70px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="38px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Time Zone Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2} className="mt-2">
                    <TextSkeleton width="80px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="45px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Currency Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="70px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="100%"
                      height="38px"
                      style={{ borderRadius: "4px" }}
                    />
                  </Col>
                </Row>

                {/* Communication Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="100px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <div className="d-flex gap-4">
                      <TextSkeleton width="80px" height="20px" />
                      <TextSkeleton width="80px" height="20px" />
                    </div>
                  </Col>
                </Row>

                {/* Allow Marketing Skeleton */}
                <Row className="mb-3 align-items-center">
                  <Col md={2}>
                    <TextSkeleton width="110px" height="20px" />
                  </Col>
                  <Col md={6}>
                    <TextSkeleton
                      width="60px"
                      height="24px"
                      style={{ borderRadius: "12px" }}
                    />
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="home"
      className={`tab_content font-family-inter`}
      style={{ marginTop: "-70px" }}
    >
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.3; }
            100% { opacity: 0.6; }
          }
        `}
      </style>


      <div className='edit-profile-form'>
              <div className="d-flex justify-content-between align-items-center">
                <div>{/* Breadcrumb content */}</div>
                <div className="d-flex gap-2">{/* Action buttons */}</div>
              </div>

            <Container className="scrollable-container">
            <p className="Profilesettinghead">Profile Settings</p>

              {/* Avatar Upload Section */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label className="fw-bold">Avatar</Form.Label>
                </Col>
                <Col md={9}>
                  <div className="d-flex align-items-center">
                    {/* Avatar Image */}
                    <img
                      className="rounded-circle header-profile-user me-3"
                      src={formData.avatar || defaultAvatar}
                      alt="User Avatar"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        border: "1px solid #dee2e6",
                      }}
                    />

                    {/* Avatar Buttons */}
                    <div className="d-flex gap-3 align-items-center">
                      <CustomButton
                        onClick={handleButtonClick}
                        text={
                          formData.avatar ? "Change Avatar" : "Upload Avatar"
                        }
                        isEditEnabled={true}
                        isDisabled={isLoading}
                        iconClass={formData.avatar ? "fa-sync" : "fa-upload"}
                        variant="primary"
                      />
                      <CustomButton
                        onClick={removeAvatar}
                        text="Remove Avatar"
                        isEditEnabled={!!formData.avatar}
                        isDisabled={!formData.avatar || isLoading}
                        iconClass="fa-trash-alt"
                        variant={formData.avatar ? "danger" : "secondary"}
                      />
                    </div>

                    {/* Hidden file input */}
                    <Form.Control
                      id="avatar-upload"
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleAvatarChange}
                      className="d-none"
                    />
                  </div>
                  <div className="d-flex align-items-center mt-1">
                    <div className="d-flex align-items-start">
                      <div
                        className=" d-flex align-items-center justify-content-center"
                        style={{
                          marginRight: "6px",
                        }}
                      >
                        <i
                          className="fas fa-info-circle IconColor"
                          style={{ fontSize: "14px", marginTop: "2px" }}
                        ></i>
                      </div>
                      <small
                        className="text-muted"
                        style={{ marginTop: "1px" }}
                      >
                        Only JPG, JPEG, and PNG formats allowed. Maximum file
                        size: 2MB.
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Full Name */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label>
                    Full Name <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                </Col>
                <Col md={6}>
                  <div className="row g-3">
                    <Col md={6}>
                      <Form.Group controlId="first_name">
                        <Form.Control
                          type="text"
                          name="first_name"
                          className="form-control highlighted-input colorfor-placeholder styled-input"
                          placeholder="First Name"
                          value={formData.first_name}
                          onChange={handleChange}
                          onBlur={() => {
                            if (errors.first_name) {
                              setErrors((prev) => ({
                                ...prev,
                                first_name: "",
                              }));
                            }
                          }}
                          isInvalid={!!errors.first_name}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.first_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="last_name">
                        <Form.Control
                          type="text"
                          name="last_name"
                          className="form-control highlighted-input colorfor-placeholder styled-input"
                          placeholder="Last Name"
                          value={formData.last_name}
                          onChange={handleChange}
                          onBlur={() => {
                            if (errors.last_name) {
                              setErrors((prev) => ({
                                ...prev,
                                last_name: "",
                              }));
                            }
                          }}
                          isInvalid={!!errors.last_name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.last_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </div>
                </Col>
              </Row>

              {/* Email */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label>
                    Email <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="email">
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Email ID"
                      className="form-control highlighted-input colorfor-placeholder styled-input "
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => {
                        if (errors.email) {
                          setErrors((prev) => ({ ...prev, email: "" }));
                        }
                      }}
                      isInvalid={!!errors.email}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Company */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label>Company</Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="company">
                    <Form.Control
                      type="text"
                      name="company"
                      className="form-control highlighted-input colorfor-placeholder styled-input "
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleChange}
                      isInvalid={!!errors.company}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Contact Phone */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label>
                    Contact Phone <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                </Col>
                <Col md={6}>
                  <div
                    className=" phone-input-container d-flex border rounded px-2 py-1 align-items-center borderEditprofilePhone justify-content-center"
                    style={{ height: "45px" }}
                  >
                    <div
                      className="d-flex align-items-center me-2 "
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setIsCountryDropdownOpen(!isCountryDropdownOpen)
                      }
                    >
                      <ReactCountryFlag
                        countryCode={selectedCountry.code}
                        svg
                        style={{
                          width: "1.5em",
                          height: "1.5em",
                          marginRight: "6px",
                          marginLeft: "6px",
                        }}
                        title={selectedCountry.code}
                      />
                      <span className="ms-1 text-muted">▾</span>
                    </div>
                    <Form.Control
                      type="text"
                      name="contact_phone"
                      placeholder="Phone Number"
                      value={
                        formData.contact_phone ||
                        selectedCountry?.dialCode ||
                        ""
                      }
                      onFocus={() => {
                        setErrors((prev) => ({ ...prev, contact_phone: "" }));
                        setPhoneTouched(false);
                      }}
                      onChange={handlePhoneChange}
                      onBlur={() => {
                        setPhoneTouched(true);

                        if (
                          !formData.contact_phone ||
                          formData.contact_phone.trim() === "" ||
                          formData.contact_phone === selectedCountry?.dialCode
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            contact_phone: "Phone number is required",
                          }));
                          return;
                        }

                        const phoneWithoutCode =
                          formData.contact_phone.replace(
                            new RegExp(
                              `^${escapeRegExp(
                                selectedCountry?.dialCode || ""
                              )}\\s?`
                            ),
                            ""
                          );
                        const digitCount = phoneWithoutCode.replace(
                          /\D/g,
                          ""
                        ).length;

                        if (digitCount < 10) {
                          setErrors((prev) => ({
                            ...prev,
                            contact_phone:
                              "Phone number must be at least 10 digits",
                          }));
                        } else {
                          setErrors((prev) => ({
                            ...prev,
                            contact_phone: "",
                          }));
                        }
                      }}
                      required
                      maxLength={17}
                      pattern="^\+?[\d\s]{0,20}$"
                      className={`border-0 shadow-none flex-grow-1 ${
                        errors.contact_phone ? "is-invalid" : ""
                      }`}
                      style={{ minWidth: "0", height: "40px" }}
                    />
                  </div>

                  {errors.contact_phone && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.contact_phone}
                    </Form.Control.Feedback>
                  )}

                  <div className="d-flex align-items-center mt-1">
                    <div className="d-flex align-items-start">
                      <div
                        className=" d-flex align-items-center justify-content-center"
                        style={{
                          marginRight: "6px",
                        }}
                      >
                        <i
                          className="fas fa-info-circle IconColor"
                          style={{ fontSize: "14px", marginTop: "2px" }}
                        ></i>
                      </div>
                      <small
                        className="text-muted"
                        style={{ marginTop: "1px" }}
                      >
                        Phone number can start with +, contain numbers and
                        optional single space (max 15 digits)
                      </small>
                    </div>
                  </div>

                  {isCountryDropdownOpen && (
                    <div
                      className="country-dropdown position-absolute bg-white border mt-1 rounded shadow"
                      style={{
                        zIndex: 1000,
                        maxHeight: "300px",
                        overflowY: "auto",
                        width: "300px",
                      }}
                    >
                      {dropdownOptions.countries.map((country) => (
                        <div
                          key={country.id}
                          className="country-option d-flex align-items-center px-3 py-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <ReactCountryFlag
                            countryCode={country.code}
                            svg
                            style={{
                              width: "1.5em",
                              height: "1.5em",
                              marginRight: "12px",
                            }}
                            title={country.code}
                          />
                          <span className="country-name flex-grow-1">
                            {country.name}
                          </span>
                          <span className="dial-code text-muted">
                            {country.dialCode}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Col>
              </Row>

              {/* Company Site */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label>Company Site</Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="company_site">
                    <Form.Control
                      type="url"
                      name="company_site"
                      className="form-control highlighted-input colorfor-placeholder styled-input "
                      placeholder="example.com"
                      value={formData.company_site}
                      onChange={handleChange}
                      onBlur={(e) => {
                        if (errors.company_site) {
                          setErrors((prev) => ({
                            ...prev,
                            company_site: "",
                          }));
                        }
                      }}
                      isInvalid={!!errors.company_site}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_site}
                    </Form.Control.Feedback>
                    <Form.Text
                      as="div"
                      className="d-flex align-items-center gap-1"
                    >
                      <i
                        className="fas fa-info-circle IconColor"
                        style={{ fontSize: "14px" }}
                      ></i>
                      <span className="text-muted">
                        You can enter with or without https:// (e.g.,
                        example.com)
                      </span>
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Country */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label className="mb-0">Country</Form.Label>
                </Col>
                <Col md={6} className="d-flex flex-column">
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="bordercolourcustom"
                  >
                    <option value="">Select a Country</option>
                    {dropdownOptions.countries
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* Language */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label className="mb-0">Language</Form.Label>
                </Col>
                <Col md={6} className="d-flex flex-column">
                  <Form.Select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    className="bordercolourcustom"
                  >
                    <option value="">Select a Language</option>
                    {dropdownOptions.languages
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((language) => (
                        <option key={language.id} value={language.id}>
                          {language.name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* Time Zone */}
              <Row className="mb-3 align-items-center">
                <Col md={2} className="mt-2">
                  <Form.Label>Time Zone</Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    name="time_zone"
                    className="form-control highlighted-input colorfor-placeholder styled-input"
                    value="UTC"
                    readOnly
                  />
                </Col>
              </Row>

              {/* Currency */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label className="mb-0 ">Currency</Form.Label>
                </Col>
                <Col md={6} className="d-flex flex-column">
                  <Form.Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="bordercolourcustom"
                  >
                    <option value="">Select a Currency</option>
                    {dropdownOptions.currencies
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((currency) => (
                        <option key={currency.id} value={currency.id}>
                          {currency.name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* Communication */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label className="mb-0" style={{ whiteSpace: "nowrap" }}>
                    Communication <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                </Col>
                <Col md={6} className="d-flex flex-column">
                  <Form.Group controlId="communication">
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="checkbox"
                        id="email-communication"
                        label="Email"
                        name="communication"
                        value="Email"
                        checked={formData.communication.includes("Email")}
                        onChange={handleChange}
                      />
                      <Form.Check
                        type="checkbox"
                        id="phone-communication"
                        label="Phone"
                        name="communication"
                        value="Phone"
                        checked={formData.communication.includes("Phone")}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.communication && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {errors.communication}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Allow Marketing */}
              <Row className="mb-3 align-items-center">
                <Col md={2}>
                  <Form.Label className="mb-0">Allow Marketing</Form.Label>
                </Col>
                <Col md={6} className="d-flex flex-column">
                  <Form.Check
                    type="switch"
                    id="allow_marketing"
                    label=""
                    name="allow_marketing"
                    checked={formData.allow_marketing}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            </Container>
            
           </div> 
      </div>
  );
};

export default EditProfile;